import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import FontAwesome from 'react-fontawesome';
import classNames from 'classnames';

import {
	removeConversationRequest,
	pauseOrResumeConversationRequest,
	canDowloandFiles,
	canEditConversation,
	hasBlockingMissingMedias,
	getMissingMediasRecoveryPath,
	CONVERSATION_STATUS_PENDING,
	CONVERSATION_STATUS_ONVALIDATION,
	CONVERSATION_STATUS_DONE,
} from '../../ConversationActions';
import { getOrder } from '../../../Order/OrderActions';
import { confirmAction, displayErrors } from '../../../Error/ErrorActions';

import withRouter from '../../../../components/Router/WithRouter';

function ConversationCardActions({ dispatch, navigate, intl, conversation, conversationOrders = [] }) {
	const [isFetching, setIsFetching] = useState(false);

	const currentOrder = conversationOrders.find(order => ['pending', 'onpreparation'].includes(order?.status));
	const blockingMissingMedias = hasBlockingMissingMedias(conversation);

	const canEdit = () => canEditConversation(conversation, conversationOrders);

	const handleRemove = () => {
		confirmAction(
			intl.formatMessage({
				id: 'conversationRemoveConfirmTitle',
				defaultMessage: 'Remove conversation',
			}),
			intl.formatMessage({
				id: 'conversationRemoveConfirmText',
				defaultMessage: 'By removing your conversation, your medias will be removed and can not be retrived!',
			}),
			() => {
				setIsFetching(true);
				dispatch(removeConversationRequest(conversation._id)).then(result => {
					setIsFetching(false);
					if(result) {
						displayErrors('success', intl.formatMessage({
							id: 'conversationRemoveSuccess',
							defaultMessage: 'Conversation removed!',
						}));
					} else {
						displayErrors('error', intl.formatMessage({
							id: 'alertGenericError',
							defaultMessage: 'An error occured! Please try later',
						}));
					}
				});
			},
		);
	};

	const handlePauseOrResumeOrder = () => {
		const isResume = currentOrder?.status === 'pending';
		confirmAction(
			'',
			isResume
				? (
					blockingMissingMedias
						? intl.formatMessage({
							id: 'conversationResumeMissingMediasConfirmText',
							defaultMessage: 'Some photos are still missing. Your book will go to print without them. Prefer completing the medias if you can.',
						})
						: intl.formatMessage({ id: 'conversationResumeConfirmText', defaultMessage: 'Your conversation will be marked as verified and will be generated to be printed.' })
				)
				: intl.formatMessage({ id: 'conversationPauseConfirmText', defaultMessage: 'The generation of your books will be paused. You can resume it at any time.' }),
			() => {
				setIsFetching(true);
				dispatch(pauseOrResumeConversationRequest(conversation._id, currentOrder?._id, isResume)).then(() => {
					setIsFetching(false);
				});
			},
		);
	};

	const handleHighlights = () => {
		navigate(`/conversation/${conversation._id}/highlights`);
	};

	const canRemove = [CONVERSATION_STATUS_PENDING, CONVERSATION_STATUS_DONE].includes(conversation.status);

	return (
		<div className="conversations-lux-card__actions">
			{blockingMissingMedias && (
				<Button
					color="warning"
					className="btn-atelier w-100"
					disabled={isFetching}
					onClick={() => navigate(getMissingMediasRecoveryPath(conversation._id))}
					data-testid="conversation-missing-medias-cta"
				>
					<FontAwesome name="exclamation-triangle" />{' '}
					<FormattedMessage id="conversationMissingMediasCta" defaultMessage="Complete missing medias" />
				</Button>
			)}

			{canEdit() && (
				<Button
					className={classNames('w-100', blockingMissingMedias ? 'btn-atelier' : 'btn-atelier-primary')}
					outline={blockingMissingMedias}
					disabled={isFetching}
					onClick={() => navigate(`/conversation/${conversation._id}/preview`)}
				>
					<FormattedMessage id="conversationEditStart" defaultMessage="Edit Book" /> <FontAwesome name="chevron-right" />
				</Button>
			)}

			<Button color="warning" className="btn-atelier-gold w-100" disabled={isFetching} onClick={handleHighlights}>
				{isFetching ? <FontAwesome name="spinner" spin /> : <FontAwesome name="bar-chart" />} <FormattedMessage id="conversationHighlightShareButtonAlt" defaultMessage="Discover your conversation stats" />
			</Button>

			{canDowloandFiles(conversation) && (
				<Button className="btn-atelier w-100" disabled={isFetching} tag={Link} to={`/conversation/${conversation._id}/download`}>
					<FontAwesome name="download" /> <FormattedMessage id="conversationDownloadPdfButton" defaultMessage="Download E-Book" />
				</Button>
			)}

			{currentOrder && conversation.status === CONVERSATION_STATUS_ONVALIDATION && (
				<Button
					className={classNames(
						'btn-atelier w-100',
						currentOrder.status === 'pending'
							? 'conversations-lux-card__action--resume'
							: 'conversations-lux-card__action--pause',
					)}
					outline={blockingMissingMedias && currentOrder.status === 'pending'}
					disabled={isFetching}
					onClick={handlePauseOrResumeOrder}
				>
					{
						currentOrder.status === 'pending'
						? (
							<span>
								{isFetching ? <FontAwesome name="spinner" spin /> : <FontAwesome name="play" />}{' '}
								{
									blockingMissingMedias
										? <FormattedMessage id="conversationResumeWithoutMediasButton" defaultMessage="Continue without these photos" />
										: <FormattedMessage id="conversationResumeButton" defaultMessage="Approve & Print" />
								}
							</span>
						)
						: <span>{isFetching ? <FontAwesome name="spinner" spin /> : <FontAwesome name="pause" />} <FormattedMessage id="conversationPauseButton" defaultMessage="Pause this conversation" /></span>
					}
				</Button>
			)}

			{canRemove && (
				<Button color="link" size="sm" className="w-100" onClick={handleRemove} disabled={isFetching}>
					{isFetching ? <FontAwesome name="spinner" spin /> : <FontAwesome name="times" />} <FormattedMessage id="conversationRemoveButton" defaultMessage="Remove conversation" />
				</Button>
			)}
		</div>
	);
}

ConversationCardActions.propTypes = {
	dispatch: PropTypes.func.isRequired,
	navigate: PropTypes.func.isRequired,
	intl: PropTypes.object.isRequired,
	conversation: PropTypes.object.isRequired,
	conversationOrders: PropTypes.arrayOf(PropTypes.object),
};

ConversationCardActions.defaultProps = {
	conversationOrders: [],
};

function mapStateToProps(store, props) {
	return {
		conversationOrders: (props.conversation.orders || []).map(order => getOrder(store, order?._id || order) || (typeof order === 'object' ? order : null)).filter(Boolean),
	};
}

export default connect(mapStateToProps)(withRouter(injectIntl(ConversationCardActions)));
